import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type IProjectGroupCompletionItem from "../dataTypes/IProjectGroupCompletionItem";
import Trophy from "../../../common/components/Trophy/Trophy";

// Pre-defined status icons to avoid recreating JSX on each render
const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3 w-3"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3 w-3"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const DotsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3 w-3"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const TrophyIcon = () => <Trophy width={12} height={12} fill="#15803d" />;

interface EnduserModuleStatusCardProps {
  module: IProjectGroupCompletionItem;
  onClick?: () => void;
  disabled?: boolean;
}

const EnduserModuleStatusCard = memo(
  ({ module, onClick, disabled }: EnduserModuleStatusCardProps) => {
    const { t } = useTranslation();

    // Derive status from API fields - no t() in dependencies
    const { statusKey, statusColor, statusBgColor, isCompleted, hasStarted } = useMemo(() => {
      // Determine completion: progressPercentage === 100 or isPerfectScore
      const isCompleted = module.progressPercentage === 100 || module.isPerfectScore === true;
      // Determine if started: has a dateLastSession or progressPercentage > 0
      const hasStarted =
        !!module.dateLastSession ||
        (module.progressPercentage !== null && module.progressPercentage > 0);

      if (isCompleted) {
        return {
          statusKey: "completed" as const,
          statusColor: "text-green-700",
          statusBgColor: "bg-green-100",
          isCompleted: true,
          hasStarted: true,
        };
      }
      if (hasStarted) {
        return {
          statusKey: "inProgress" as const,
          statusColor: "text-yellow-700",
          statusBgColor: "bg-yellow-100",
          isCompleted: false,
          hasStarted: true,
        };
      }
      return {
        statusKey: "notStarted" as const,
        statusColor: "text-gray-600",
        statusBgColor: "bg-gray-100",
        isCompleted: false,
        hasStarted: false,
      };
    }, [module.progressPercentage, module.isPerfectScore, module.dateLastSession]);

    // Get translated status text
    const status = t(`enduserStats.status.${statusKey}`);

    // Get status icon based on key - trophy for perfect score, checkmark for regular completion
    const StatusIcon =
      statusKey === "completed" && module.isPerfectScore
        ? TrophyIcon
        : statusKey === "completed"
        ? CheckIcon
        : statusKey === "inProgress"
        ? ClockIcon
        : DotsIcon;

    // Use projectThumbnail from API (not thumbnail)
    const thumbnailUrl = module.projectThumbnail?.s3_url;

    // Parse score and determine color based on percentage
    const { hasScore, scoreColor } = useMemo(() => {
      if (!module.score) return { hasScore: false, scoreColor: "" };

      const parts = module.score.split("/");
      if (parts.length === 2) {
        const score = parseInt(parts[0], 10);
        const max = parseInt(parts[1], 10);
        if (max > 0) {
          const pct = (score / max) * 100;
          if (pct >= 80) return { hasScore: true, scoreColor: "text-green-400" };
          if (pct >= 50) return { hasScore: true, scoreColor: "text-yellow-400" };
          return { hasScore: true, scoreColor: "text-red-400" };
        }
      }
      return { hasScore: true, scoreColor: "text-white/90" };
    }, [module.score]);

    // Module is disabled if it has no data (not started and no score)
    const isDisabled = disabled ?? (!hasStarted && !hasScore);

    return (
      <div
        onClick={isDisabled ? undefined : onClick}
        className={`relative group rounded-xl overflow-hidden shadow transition-all ${
          isDisabled
            ? "cursor-default opacity-70"
            : "cursor-pointer hover:shadow-lg hover:scale-[1.02]"
        }`}
        style={{ width: "180px", height: "120px" }}
      >
        {/* Background image or gradient */}
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={module.projectName}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-2 right-8 w-12 h-12 border-2 border-white rounded-full" />
              <div className="absolute bottom-8 left-3 w-6 h-6 border-2 border-white rounded-full" />
            </div>
          </div>
        )}

        {/* Dark overlay for text readability - stronger gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Content */}
        <div className="absolute inset-0 p-3 flex flex-col justify-end">
          {/* Name */}
          <h3
            className="text-white font-semibold text-sm leading-tight line-clamp-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
            title={module.projectName}
          >
            {module.projectName}
          </h3>
          {/* Score info */}
          <div className="text-xs mt-0.5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
            {hasScore ? (
              <span className="text-white/90">
                {t("statsNew.module.bestScore")}:{" "}
                <span className={`font-semibold ${scoreColor}`}>{module.score}</span>
              </span>
            ) : (
              <span className="italic text-white/90">{t("enduserStats.noScore")}</span>
            )}
          </div>
        </div>

        {/* Status badge */}
        <div className="absolute top-2 left-2">
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[10px] font-medium shadow ${statusBgColor} ${statusColor}`}
          >
            <StatusIcon />
            {status}
          </span>
        </div>

        {/* Progress indicator (hidden when 100% since "Terminé" badge already indicates completion) */}
        {module.progressPercentage !== null &&
          module.progressPercentage > 0 &&
          module.progressPercentage < 100 && (
            <div className="absolute top-2 right-2">
              <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 bg-white/90 text-gray-800 text-xs font-bold rounded-lg shadow">
                {Math.round(module.progressPercentage)}%
              </span>
            </div>
          )}

        {/* Progress bar at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
          <div
            className={`h-full transition-all duration-300 ${
              isCompleted ? "bg-green-500" : hasStarted ? "bg-yellow-500" : "bg-transparent"
            }`}
            style={{ width: `${module.progressPercentage ?? 0}%` }}
          />
        </div>
      </div>
    );
  },
);

EnduserModuleStatusCard.displayName = "EnduserModuleStatusCard";

export default EnduserModuleStatusCard;
